/**
 * O relógio da demonstração, no topo (`O-03`, ADR-059).
 *
 * Duas coisas, e as duas só em organização de demonstração:
 *
 * - **o selo**, para todo mundo da organização, quando o relógio dela está à frente do
 *   servidor: "+8 d · 17/10, 14:32". Sem ele, quem entra no meio da apresentação veria prazos
 *   vencendo "do nada" — e é exatamente o que o tempo da demonstração faz;
 * - **o avanço** — 24 h, 7, 8, 15 e 30 dias —, só para o apresentador e o administrador
 *   (`O-06`), e só onde o
 *   dono do banco liberou (`cockpit_api.relogio_virtual`). O avanço não volta: a confirmação
 *   diz isso antes, e não depois.
 *
 * Quem lê e avança é a moldura (`useRelogioDaOrganizacao`); aqui só se mostra e se pede.
 */
import { useEffect, useState } from "react";

import { ErroDaApi } from "../api/cliente";
import type { RelogioNaTela } from "../api/tipos";
import { agoraDoInquilino, rotuloDoDeslocamento } from "../relogio";

const ROTULO_DO_AVANCO: Record<string, string> = {
  "24h": "24 horas",
  "7d": "7 dias",
  "8d": "8 dias",
  "15d": "15 dias",
  "30d": "30 dias",
};

/** O que cada avanço costuma mostrar — a frase da confirmação. */
const O_QUE_MOSTRA: Record<string, string> = {
  "24h": "o prazo de envio de 24 h vencendo",
  "7d": "o cancelamento pela SENATRAN depois de 7 dias em processamento",
  "8d": "o 1º aviso ao condutor pela CNH Digital",
  "15d": "o 2º aviso ao condutor pela CNH Digital",
  "30d": "o marco de autuação e o vencimento do pagamento",
};

// O tempo da demonstração é de quem apresenta (`O-06`, ADR-062) — e do administrador.
const QUEM_AVANCA = new Set(["APRESENTADOR", "ADMIN"]);

export function RelogioDaDemonstracao({
  relogio,
  perfil,
  aoAvancar,
}: {
  relogio: RelogioNaTela | null;
  perfil: string | undefined;
  aoAvancar: (avanco: string) => Promise<unknown>;
}) {
  const [avancando, definirAvancando] = useState(false);
  const [erro, definirErro] = useState<string | null>(null);
  const deslocamentoMs = (relogio?.deslocamento_s ?? 0) * 1000;
  const [agora, definirAgora] = useState(() => agoraDoInquilino());

  // O selo mostra a hora da demonstração andando — de meio em meio minuto basta.
  useEffect(() => {
    if (!deslocamentoMs) return;
    definirAgora(agoraDoInquilino());
    const id = window.setInterval(
      () => definirAgora(agoraDoInquilino()),
      30_000,
    );
    return () => window.clearInterval(id);
  }, [deslocamentoMs]);

  if (!relogio) return null;
  const podeAvancar =
    relogio.pode_avancar && !!perfil && QUEM_AVANCA.has(perfil);
  if (!deslocamentoMs && !podeAvancar) return null;

  async function avancar(avanco: string) {
    if (
      !window.confirm(
        `Avançar o relógio desta organização em ${ROTULO_DO_AVANCO[avanco] ?? avanco}? É para mostrar ${O_QUE_MOSTRA[avanco] ?? "os prazos vencendo"}.\n\n` +
          "O tempo passa só nesta organização, e não volta: os prazos que vencerem no tempo novo vencem de verdade.",
      )
    )
      return;
    definirAvancando(true);
    definirErro(null);
    try {
      await aoAvancar(avanco);
    } catch (falha) {
      definirErro(
        falha instanceof ErroDaApi
          ? falha.message
          : "Não foi possível avançar o relógio.",
      );
    } finally {
      definirAvancando(false);
    }
  }

  return (
    <span className="flex flex-col leading-tight">
      <span className="text-rotulo tracking-[0.06em] text-azul-claro uppercase">
        Relógio da demonstração
      </span>
      <span className="flex items-center gap-1.5">
        {deslocamentoMs > 0 && (
          <span
            title="O tempo desta organização está à frente do servidor: os prazos contam daqui."
            className="mono rounded-pilula border border-amarelo-borda bg-amarelo-fundo px-2 py-0.5 text-[11px] font-bold whitespace-nowrap text-amarelo"
          >
            {rotuloDoDeslocamento(deslocamentoMs)} ·{" "}
            {agora.toLocaleString("pt-BR", {
              day: "2-digit",
              month: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            })}
          </span>
        )}
        {podeAvancar && (
          <select
            aria-label="Avançar o relógio da demonstração"
            value=""
            disabled={avancando}
            onChange={(e) => {
              if (e.target.value) void avancar(e.target.value);
            }}
            className="rounded-botao border border-linha bg-painel-2 px-1.5 py-0.5 text-tinta-2"
          >
            <option value="">{avancando ? "Avançando…" : "Avançar…"}</option>
            {relogio.avancos.map((a) => (
              <option key={a} value={a}>
                +{ROTULO_DO_AVANCO[a] ?? a}
              </option>
            ))}
          </select>
        )}
      </span>
      {erro && (
        <span role="alert" className="text-[10.5px] text-vermelho">
          {erro}
        </span>
      )}
    </span>
  );
}
